import { Component, ElementRef, EventEmitter, inject, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AlertType, DynamicComponentService } from '@shared/components/atomics/body-loading/dynamic-component-service.service';

@Component({
  selector: 'app-so-file-drop-zone',
  templateUrl: './so-file-drop-zone.component.html',
  styleUrl: './so-file-drop-zone.component.scss'
})
export class SoFileDropZoneComponent {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  @Output() saveFilesEvent = new EventEmitter<File[]>();
  @Input() excludeMimeTypes: string[] = [];
  @Input() validateByModal = false;
  @Input() validateMarca = false;
  @Input() disabled: boolean = false;
  @Input() showMessage: boolean = false;
  @Input() showButtonSave: boolean = true;
  @Input() showMaxFileSize: boolean = false;
  @Input({required: true}) idInput: string = '';
  @Input() maxSizeMultimedia: number = 50; // MB para imágenes y videos
  @Input() maxSizeDocs: number = 50;

  public files: File[] = [];
  public isDragging = false;

  private readonly dynamicComponentService = inject(DynamicComponentService);

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  public onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files) {
    const cleanedFiles = this.cleanFileNames(Array.from(event.dataTransfer.files));
    const isValid = this.handleFiles(cleanedFiles);

    if (isValid) {
      this.saveFilesEvent.emit(cleanedFiles);
    }
  }
  }

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const cleanedFiles = this.cleanFileNames(Array.from(input.files));
      const isValid = this.handleFiles(cleanedFiles);

      if (isValid) {
        this.saveFilesEvent.emit(cleanedFiles);
      }
    }  
  }

 private handleFiles(files: FileList | File[]): boolean {
    const errorMessages: string[] = [];
    const initialLength = this.files.length;

    Array.from(files).forEach(file => {
      const maxSize = this.getMaxSizeForFile(file);
      const exceedsMaxSize = file.size > maxSize * 1024 * 1024;
      const isExcluded = this.isExcludedFileType(file);
      const isDuplicate = this.isFileDuplicate(file);

      if (exceedsMaxSize) {
        errorMessages.push(`"${file.name}" excede el tamaño máximo de ${maxSize} MB.`);
        return;
      }

      if (isExcluded) {
        errorMessages.push(`"${file.name}" tiene un tipo no permitido (${file.type}).`);
        return;
      }

      if (!isDuplicate) {
        this.files.push(file);
      }
    });

    const addedFilesCount = this.files.length - initialLength;

    if (errorMessages.length > 0) {
      this.dynamicComponentService.showAlert({
        title: 'Error al subir archivos',
        message: errorMessages.join('\n'),
        alertType: AlertType.error,
        autohide: false,
        animate: true,
        showButtons: false,
        showButtonClose: true
      });
      return false;
    }

    if (addedFilesCount === 0) {
      return false;
    }

    return true;
  }

  private getMaxSizeForFile(file: File): number {
    const docMimeTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const isDoc = docMimeTypes.includes(file.type);

    return isDoc ? this.maxSizeDocs : this.maxSizeMultimedia;
  }

  private isExcludedFileType(file: File): boolean {
    return this.excludeMimeTypes.some(excludedType => file.type.startsWith(excludedType));
  }

  private isFileDuplicate(newFile: File): boolean {
    return this.files.some(existingFile =>
      existingFile.name === newFile.name && existingFile.size === newFile.size
    );
  }

  public removeFile(index: number) {
    this.files.splice(index, 1);
  }

  public saveFiles() {
    this.saveFilesEvent.emit(this.files);
  }

  public validateModal() {
    this.saveFilesEvent.emit(this.files);
    this.clearFiles();
  }

  private clearFiles(): void {
    this.files = [];
  }

  public resetFileInput() {    
    this.fileInput.nativeElement.value = ''; // Limpia el input
    this.files = []; // Limpia la lista de archivos
  }

  private cleanFileNames(files: File[]): File[] {
    return files.map(file => {
      const cleanedFileName = file.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9.]/g, '');

      return new File([file], cleanedFileName, { type: file.type });
    });
  }

  public getIconForFile(file: File): string {
    if (!file || !file.name) return 'default-icon'; // Ícono por defecto si no hay archivo
  
    const extension = file.name.split('.').pop()?.toLowerCase(); // Obtener extensión del archivo
  
    // Mapeo de extensiones a iconos
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'image'; // Ícono para imágenes
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'ondeman_video'; // Ícono para videos
      case 'doc':
      case 'docx':
        return 'description'; 
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'border_all';
      case 'pdf':
        return 'picture_as_pdf'; // Ícono para PDFs
      default:
        return 'insert_drive_file'; // Ícono genérico para otros tipos
    }
  }

  public getColorForFile(file: File): string {
    if (!file || !file.name) return 'default-icon'; // Ícono por defecto si no hay archivo
  
    const extension = file.name.split('.').pop()?.toLowerCase(); // Obtener extensión del archivo
  
    // Mapeo de extensiones a iconos
    switch (extension) {
      case 'pdf':
        return 'var(--so-danger)'; // Ícono para PDFs
      case 'xls':
      case 'xlsx':
      case 'csv':
       return 'var(--so-success-thick)';
      default:
        return 'var(--color-icon)'; // Ícono genérico para otros tipos
    }
  }

}
